import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { CarouselAction, CarouselGetter } from '@/store/carousel/carousel.module';
import { CarouselSchema } from '@/store/carousel/carousel.validator';
import { Carousel } from '@/store/carousel/carousel.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'CarouselFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    carouselId: {
      type: Number
    },
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const fileDialog = ref();
    const isEditMode = computed(() => !!props.carouselId);
    const carousel = computed<Carousel | undefined>(() => store.getters[CarouselGetter.BY_ID](props.carouselId));
    const title = computed(() => isEditMode.value ? 'แก้ไขสไลด์โชว์' : 'เพิ่มสไลด์โชว์');
    const carouselForm = ref<InstanceType<typeof BaseModalComponent>>();
    const photoSource = ref('');

    // form
    interface Form {
      id?: number | null;
      title?: string | null;
      description?: string | null;
      image?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      description: '',
      image: '',
    });
    const validator = computed(() => CarouselSchema.validate(formData, { abortEarly: false }).error);
    const errors = reactive({
      title: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'title')),
      description: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'description')),
      image: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'image')),
    });
    const isValid = computed(() => !errors.title && !errors.description && !errors.image);
    const isLoading = computed(() => store.getters[CarouselGetter.STATUS] === 'init' || store.getters[CarouselGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.id = (isEditMode.value) ? props.carouselId : null;
        formData.title = carousel.value?.title;
        formData.description = carousel.value?.description;
        formData.image = carousel.value?.image;

        photoSource.value = (isEditMode.value)
        ? carousel.value?.image as string
        : require('@/assets/images/carousel-nopic.png');

        resolve();
      }).then(() => carouselForm.value?.show());
    };

    // hide modal
    const hide = () => {
      carouselForm.value?.hide();
    };

    // click photo
    const photoClick = () => {
      fileDialog.value?.click();
    };

    // change photo
    const photoChange = (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      const file = files?.item(0);

      if (file) {
        photoSource.value = URL.createObjectURL(file);
        formData.image = file.name;
      }
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.description = '';
        formData.image = '';
        fileDialog.value.value = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(CarouselAction.UPDATE, {
            id: formData.id,
            title: formData.title,
            description: formData.description,
            image: (fileDialog.value?.files?.length !== 0) ? fileDialog.value?.files?.item(0) as File : null,
          }).then(() => hide());
        } else {
          store.dispatch(CarouselAction.CREATE, {
            title: formData.title,
            description: formData.description,
            image: fileDialog.value?.files?.item(0) as File,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(CarouselAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      carouselForm,
      isEditMode,
      fileDialog,
      photoSource,
      errors,
      submited,
      isLoading,
      show,
      hide,
      photoClick,
      photoChange,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
